import type { FantasyContest, FantasyStandingRow } from '@brush/api-client';

/**
 * The rules a lineup has to satisfy, and every sentence the fantasy screens say.
 *
 * <b>`checkRoster` is a hand-written mirror of `FantasyRosterValidator.Check`</b>, and the
 * relationship is the one `points/validate.ts` has to the server's definition validator: feedback
 * as an entrant builds, never the control. The server checks the same rules on `POST /validate` and
 * again on the write, and its answer is what the screen shows wherever it is current — this exists
 * so the budget meter moves on a click rather than on a round trip, and so Submit is closed before
 * anybody presses it.
 *
 * <b>The two must agree, and a disagreement fails in one direction that matters:</b> a lineup this
 * says is legal and the server refuses is a submission that bounces off a screen showing no
 * problems. So the rules below are in the server's order, with the server's boundaries, and the
 * codes are the server's own. They were checked against it case by case — see the phase's gate
 * report.
 *
 * <b>Written for a poker player</b> (§9.3). No percentile, no snapshot number, no field name from
 * the schema. "Your team is 12 points behind", not a delta.
 */

// ---------------------------------------------------------------- the roster rules

/**
 * One rule a lineup broke.
 *
 * The code is the routing decision rather than a taxonomy — the server's note says it best: over
 * budget means drop somebody expensive, wrong size means add or remove one, not in the pool means
 * that player is not draftable at all. One `invalid_roster` would be the same sentence for five
 * different next actions.
 */
export interface RosterProblem {
  code: string;
  message: string;
}

export interface RosterCheck {
  isValid: boolean;
  salarySpent: number;
  problems: RosterProblem[];
}

export interface RosterInput {
  rosterSize: number;
  budget: number;
  budgetUnitLabel: string | null;
  captainEnabled: boolean;
  /** Player id to price, from the frozen pool. A lineup never says what its players cost. */
  pool: ReadonlyMap<string, number>;
  /** Names, for the messages. A problem naming a GUID is a problem nobody can act on. */
  names: ReadonlyMap<string, string>;
  playerIds: readonly string[];
  captainPlayerId: string | null;
}

export function checkRoster(input: RosterInput): RosterCheck {
  const problems: RosterProblem[] = [];
  const name = (id: string) => input.names.get(id) ?? id;

  // Duplicates first, because every rule below counts members and a repeat would make all of them
  // report on a roster nobody sent. The server says the same and for a sharper reason: a duplicate
  // reaching the write is refused by EF's change tracker as a 500 rather than as a 400.
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const id of input.playerIds) {
    if (seen.has(id)) duplicates.add(id);
    seen.add(id);
  }

  for (const id of duplicates) {
    problems.push({
      code: 'duplicate_member',
      message: `${name(id)} is on this roster more than once. A player can be drafted once per team.`,
    });
  }

  const distinct = [...seen];

  // Exact, not "at most". A short roster is not a cheap one — it is somebody who has not finished,
  // and accepting it would put an incomplete lineup into standings against complete ones.
  if (distinct.length !== input.rosterSize) {
    problems.push({
      code: 'roster_size',
      message: `This contest asks for ${playersLabel(input.rosterSize)} and this roster has ${playersLabel(distinct.length)}.`,
    });
  }

  for (const id of distinct) {
    if (input.pool.has(id)) continue;
    problems.push({
      code: 'player_not_in_pool',
      message: `${name(id)} is not in this contest's pool and cannot be drafted. The pool is frozen at publish, so it is the same list for everybody.`,
    });
  }

  // Exactly one, and only where the contest has captains. Both halves matter: a captain on a
  // contest that has none would be stored as a role the projection ignores, which is a roster that
  // scores differently from the one the entrant thinks they built.
  if (input.captainEnabled) {
    if (input.captainPlayerId === null) {
      problems.push({
        code: 'captain_required',
        message: "This contest multiplies one member's score, so a captain has to be named.",
      });
    } else if (!distinct.includes(input.captainPlayerId)) {
      problems.push({
        code: 'captain_required',
        message: `${name(input.captainPlayerId)} is named as captain but is not on this roster.`,
      });
    }
  } else if (input.captainPlayerId !== null) {
    problems.push({
      code: 'captain_not_allowed',
      message: 'This contest has no captain, so naming one would store a role nothing reads.',
    });
  }

  // Last, and computed over what the pool actually prices — a player who is not in it contributes
  // nothing, so an over-budget message on a roster with an undraftable member would understate the
  // real cost. That roster is already refused above.
  const salarySpent = distinct.reduce((total, id) => total + (input.pool.get(id) ?? 0), 0);

  if (salarySpent > input.budget) {
    problems.push({
      code: 'budget_exceeded',
      message: `This roster costs ${formatAmount(salarySpent, input.budgetUnitLabel)} against a budget of ${formatAmount(input.budget, input.budgetUnitLabel)}.`,
    });
  }

  return { isValid: problems.length === 0, salarySpent, problems };
}

/** Mirrors the server's `Players`: "1 player", "8 players". */
export function playersLabel(count: number): string {
  return count === 1 ? '1 player' : `${count} players`;
}

/**
 * A cost, in whatever the contest counts in — the server's `Amount`, to two decimal places at most.
 *
 * The label is presentation and may be absent, so a bare number is the fallback rather than an
 * invented currency symbol. `"0.##"` in .NET trims trailing zeroes, which is what this does.
 */
export function formatAmount(value: number, unitLabel: string | null): string {
  const number = String(Math.round(value * 100) / 100);
  const label = unitLabel?.trim();
  return label ? `${number} ${label}` : number;
}

/** The budget's unit for prose, defaulting the way room-web's does. */
export function budgetUnit(contest: Pick<FantasyContest, 'budgetUnitLabel'>): string {
  const label = contest.budgetUnitLabel?.trim();
  return label && label.length > 0 ? label : 'units';
}

// ---------------------------------------------------------------- §6's lifecycle, for a player

export type ContestStatus = 'draft' | 'open' | 'locked' | 'closed';

export function statusOf(contest: Pick<FantasyContest, 'status'>): ContestStatus {
  const known: ContestStatus[] = ['draft', 'open', 'locked', 'closed'];
  return known.includes(contest.status as ContestStatus)
    ? (contest.status as ContestStatus)
    : 'draft';
}

/**
 * What the contest is doing, said to the person deciding whether to enter.
 *
 * <b>Deliberately not room-web's labels.</b> The gallery reads "Taking entries" as a fact about a
 * thing it is running; here the same state is an invitation with a deadline, and "Closed" is the
 * one word a player must not misread as "finished". Same column, two readers — the split
 * `memberships.ts` already records between the two apps' wording of one audit column.
 */
export const STATUS_LABEL: Record<ContestStatus, string> = {
  draft: 'Not open yet',
  open: 'Open for entries',
  locked: 'Underway',
  closed: 'Finished',
};

/** Whether an entrant may still build or change a lineup. */
export function isOpenForEntry(contest: Pick<FantasyContest, 'status'>): boolean {
  return statusOf(contest) === 'open';
}

/** Whether there are standings to read. §6 puts the first snapshot at lock. */
export function hasStandings(contest: Pick<FantasyContest, 'status'>): boolean {
  const status = statusOf(contest);
  return status === 'locked' || status === 'closed';
}

// ---------------------------------------------------------------- times

/**
 * A contest's lock time, as the room's own clock reads it (§9.3).
 *
 * <b>Rendered in UTC on purpose, and this needs its reason stated.</b> Nothing in the product
 * models a room's timezone — a tournament carries a date and a wall-clock time with no offset, and
 * `FantasyContestService.StartOf` reads that pair *as UTC* to compute `LocksAt`. So for the
 * ordinary contest, whose lock time is the start of its first event, the stored instant is the
 * room's own wall clock wearing a UTC label, and formatting it back in UTC recovers exactly the
 * time printed on the room's schedule. Rendering it in the reader's browser zone would shift it by
 * their offset and show a player at the room a time nobody there recognises.
 *
 * <b>The gap this leaves</b>: an operator who typed a lock time by hand in room-web had it
 * converted from *their* browser's zone, so that value is a genuine instant and this over-corrects
 * it by their offset from UTC. Both readings cannot be right at once, and the default path is the
 * one the spec makes normal. Giving `Room` a timezone closes it properly, and
 * `FantasyContestService.StartOf` is the one place on the server that then changes.
 *
 * No zone suffix is printed, because "6:00 PM" is what a player at the room is comparing against
 * the schedule on the wall — and a "UTC" beside it would be, for them, actively wrong.
 */
export function formatLockTime(iso: string | null): string {
  if (!iso) return 'when the first event starts';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return 'when the first event starts';

  return at.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

/** An ISO instant as a reading time, in the reader's own zone — for "updated ..." and nothing else. */
export function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return at.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

// ---------------------------------------------------------------- standings movement

/**
 * How one team has moved since the previous snapshot (§9.2).
 *
 * `null` for a team that was not on the previous one — which is not "no change", and the two are
 * worded apart.
 */
export interface Movement {
  /** Positive is up the board. A rank of 5 becoming 2 is `+3`. */
  places: number;
  points: number;
}

export function movementFor(
  row: Pick<FantasyStandingRow, 'teamId' | 'rank' | 'points'>,
  previous: readonly Pick<FantasyStandingRow, 'teamId' | 'rank' | 'points'>[] | null,
): Movement | null {
  if (previous === null) return null;
  const before = previous.find((p) => p.teamId === row.teamId);
  if (!before) return null;

  return { places: before.rank - row.rank, points: row.points - before.points };
}

/**
 * Movement as a phrase, or null where there is nothing to say.
 *
 * §9.3: no deltas and no signed numbers. "Up 2" is what somebody says out loud; "+2 (rank)" is
 * what a spreadsheet says. A team that gained points without moving is still worth reporting —
 * everybody scored, and standing still while scoring is a different fact from not scoring.
 */
export function describeMovement(movement: Movement | null): string | null {
  if (movement === null) return null;
  if (movement.places > 0) return `Up ${movement.places}`;
  if (movement.places < 0) return `Down ${-movement.places}`;
  if (movement.points > 0) return 'Held';
  return null;
}

/**
 * The gap between one team and the lead, in the words §9.3 asks for.
 *
 * Shown for the reader's own team and nowhere else: it is the sentence somebody wants about
 * themselves, and printing it on every row would be a column of arithmetic nobody reads.
 */
export function describeGap(row: { rank: number; points: number }, leaderPoints: number): string {
  if (row.rank === 1) return 'Leading';
  const behind = leaderPoints - row.points;
  if (behind <= 0) return 'Level with the lead';
  return `${formatPoints(behind)} ${behind === 1 ? 'point' : 'points'} behind the lead`;
}

/** Points as a player reads them. Trailing zeroes go; real precision stays. */
export function formatPoints(points: number): string {
  return String(Math.round(points * 10000) / 10000);
}

// ---------------------------------------------------------------- §6.2's Hold

/**
 * §6.2's pause, said plainly (§9.2: "this is the one state where 'no update' is not the same as
 * 'no change'").
 *
 * <b>The fact and nothing else.</b> The contest carries a boolean and a moment; which events
 * changed lives on `GET /{id}/drift`, which is admin-only and which this app must not call — a
 * player has no business seeing the event list, and the request would be a 403 that reads as a
 * broken app.
 */
export function pausedNotice(since: string | null): string {
  const when = since === null ? null : formatWhen(since);

  return (
    'The list of events this contest scores changed after entries closed, so scoring is on hold ' +
    'while the room sorts it out. Nothing below has moved since' +
    (when === null ? '.' : ` ${when}.`) +
    ' Your lineup and everybody else’s are unaffected.'
  );
}

/**
 * §6.2's amendment, said to the person it happened to.
 *
 * <b>An entrant is owed this more than an operator is.</b> They built a roster against a stated
 * list of events and the standings they are looking at were computed under a different one — which
 * is exactly the change §6.2 refuses to make silently. Saying only "updated 3 hours ago" would make
 * an amended board indistinguishable from an ordinary recalculation.
 *
 * <b>It says what happened, not who or why.</b> The reason lives on the snapshot and the event list
 * lives on the admin-only drift route; neither is an entrant's, for the same reason the pause
 * notice carries no events. What an entrant needs is that the goalposts moved and that their lineup
 * was not touched.
 */
export function amendedNotice(): string {
  return (
    'The room changed which events this contest scores after entries closed, and an administrator ' +
    'brought the standings up to date under the new list. Your lineup is exactly as you left it — ' +
    'what changed is which results count towards it.'
  );
}

// ---------------------------------------------------------------- the disclosure

/**
 * The three things §9.2 requires an entrant to be told before they submit, and the third is the
 * one this phase is asked not to bury.
 *
 * <b>"A rostered player who does not enter any event scores zero" is the central risk of poker
 * fantasy</b> and the most likely support question in the feature. It is not a footnote and not a
 * tooltip: a fantasy roster in any other sport is drawn from people who are contractually going to
 * play, and a poker player who decides not to turn up is not doing anything wrong. Somebody who
 * learns this after the deadline has lost the contest to a rule nobody told them.
 *
 * Held here rather than inline so the three read as one set and can be checked in one place.
 */
export function submissionDisclosures(contest: FantasyContest): readonly {
  key: string;
  heading: string;
  body: string;
}[] {
  return [
    {
      key: 'zero',
      heading: 'A player who doesn’t turn up scores you nothing',
      body:
        'Everybody on your roster only scores if they actually enter an event this contest counts. ' +
        'Nobody is obliged to play, and there is no substitution — if one of your picks sits the ' +
        'series out, they score zero and your team carries it. Spreading your budget is how people ' +
        'protect against that.',
    },
    {
      key: 'lock',
      heading: `Entries close ${formatLockTime(contest.locksAt)}`,
      body:
        'That is when the first event starts, unless the room brought it forward. After it, ' +
        'scoring begins and the standings start moving.',
    },
    {
      key: 'final',
      heading: 'Your lineup is final once entries close',
      body:
        'You can change it as often as you like until then. Afterwards it cannot be edited, ' +
        'swapped or withdrawn — there are no trades and no waivers.',
    },
  ];
}
